import { useState } from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import Header from "../Header";
import Sidebar from "../Sidebar";
import Footer from "../Footer";
import Spinner from "../common/Spinner";
import { updatePassword } from "../../redux/actions/userActions";

const UserPassword = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [newPasswordVisible, setNewPasswordVisible] = useState(false);
    const [formState, setFormState] = useState({
        values: { current_password: "", new_password: "" },
        errors: { current_password: "Password Required", new_password: "Password Required" },
    });

    const validatePassword = (name, password) => {
        if (!password) {
            return name === "current_password"
                ? "Current password is required"
                : "New password is required";
        }
        if (password.length < 6 || password.length > 15) {
            return name === "current_password"
                ? "Current password must be between 6 and 15 characters"
                : "New password must be between 6 and 15 characters";
        }
        return null;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormState((prev) => ({
            values: { ...prev.values, [name]: value },
            errors: { ...prev.errors, [name]: validatePassword(name, value) },
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const { current_password, new_password } = formState.values;

        if (formState.errors.current_password) {
            toast.error(formState.errors.current_password);
            return;
        }
        if (formState.errors.new_password) {
            toast.error(formState.errors.new_password);
            return;
        }

        setSubmitted(true);
        setLoading(true);
        dispatch(updatePassword(current_password, new_password)).then(() => {
            setFormState({
                values: { current_password: "", new_password: "" },
                errors: {},
            });
            setSubmitted(false);
            setLoading(false);
        });
    };

    return (
        <>
            <div className="container-scroller">
                <Header />
                <div className="container-fluid page-body-wrapper">
                    <Sidebar />
                    <Box component="main" className="main-panel" sx={{ display: "flex", flexDirection: "column" }}>
                        <Box sx={{ flex: 1, py: { xs: 2, md: 3 } }}>
                            <Container maxWidth="md" disableGutters sx={{ px: { xs: 2, md: 3 } }}>
                                <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, border: "1px solid", borderColor: "divider" }}>
                                    <Typography variant="h3" component="h1" sx={{ mb: 3 }}>Update password</Typography>

                                    <form onSubmit={handleSubmit} noValidate autoComplete="off">
                                        <Stack spacing={3}>
                                            <TextField
                                                label="Current password"
                                                type={passwordVisible ? "text" : "password"}
                                                name="current_password"
                                                required
                                                fullWidth
                                                autoFocus
                                                autoComplete="current-password"
                                                value={formState.values.current_password || ""}
                                                onChange={handleChange}
                                                error={Boolean(submitted && formState.errors.current_password)}
                                                helperText={
                                                    (submitted && formState.errors.current_password) || " "
                                                }
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setPasswordVisible((v) => !v)}
                                                                edge="end"
                                                                aria-label={passwordVisible ? "Hide password" : "Show password"}
                                                            >
                                                                {passwordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />

                                            <TextField
                                                label="New password"
                                                type={newPasswordVisible ? "text" : "password"}
                                                name="new_password"
                                                required
                                                fullWidth
                                                autoComplete="new-password"
                                                value={formState.values.new_password || ""}
                                                onChange={handleChange}
                                                error={Boolean(submitted && formState.errors.new_password)}
                                                helperText={
                                                    (submitted && formState.errors.new_password) || " "
                                                }
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setNewPasswordVisible((v) => !v)}
                                                                edge="end"
                                                                aria-label={newPasswordVisible ? "Hide password" : "Show password"}
                                                            >
                                                                {newPasswordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />

                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                <Button type="submit" variant="contained" size="large" sx={{ minWidth: 160 }} disabled={loading}>
                                                    {loading ? "Saving…" : "Save new password"}
                                                </Button>
                                                <Button component={RouterLink} to="/apps" variant="outlined" size="large" sx={{ minWidth: 140 }}>
                                                    Cancel
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    </form>
                                </Paper>
                            </Container>
                        </Box>
                        <Footer />
                    </Box>
                </div>
            </div>
            {loading && <Spinner fullscreen label="Updating password…" />}
        </>
    );
};

export default UserPassword;
